import React from 'react';
import { Progress } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCoins } from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import {
    createDataSource, createGridOptions, DataGrid, booleanCellRenderer, LinkCellRenderer, TextFilterDefaults, indexCellRenderer,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import CommonContext, { ApiRoutes, AppNavPaths } from '../Common';
import { PageHeading, PageWrap } from '../common/forms/FormElements';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';

class ChargeTypeIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            rowData: [],
            selectedChargeType: {},
            selectedException: null,
            selectedRow: null,
        };
        this.onAddChargeType = this.onAddChargeType.bind(this);
        this.yesNoCellRenderer = this.yesNoCellRenderer.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    onAddChargeType = () => {
        this.context.history.push(`${AppNavPaths.ChargeTypeNew}`);
    };

    onSelectionChanged = (event) => {
        const selection = event.api.getSelectedRows();
        this.setState({ selectedRow: selection.length ? selection[0] : null });
    };

    async populateState() {
        const [groups, equipmentTypes, units, resourceTypes] = await Promise.all([
            httpClient.js(ApiRoutes.auth.groups()),
            httpClient.js(ApiRoutes.typeAheads.equipmentTypes()),
            httpClient.js(ApiRoutes.chargeTypeUnit.all()),
            httpClient.js(ApiRoutes.typeAheads.jobResourceTypes()),
        ]);

        const gridOptions = createGridOptions(this);

        gridOptions.components = {
            selectFilter: DataGridSelectFilter,
            selectFloatingFilter: DataGridSelectFloatingFilter,
            nameRenderer: LinkCellRenderer,
        };

        const unitsFilterParams = {
            suppressFilterButton: true,
            options: units,
            optionsLabel: 'label',
            optionsValue: 'value',
        };
        const groupFilterParams = {
            suppressFilterButton: true,
            options: groups,
            optionsLabel: 'groupName',
            optionsValue: 'id',
        };
        const equipmentFilterParams = {
            suppressFilterButton: true,
            options: equipmentTypes,
            optionsLabel: 'label',
            optionsValue: 'value',
        };
        const billableFilterParams = {
            suppressFilterButton: true,
            options: [{ name: 'Yes', id: 'true' }, { name: 'No', id: 'false' }],
            optionsLabel: 'name',
            optionsValue: 'id',
        };

        const resourceTypeFilterParams = {
            suppressFilterButton: true,
            options: resourceTypes,
            optionsLabel: 'label',
            optionsValue: 'value',
        };

        const resourceTypeLookup = Object.fromEntries(
            resourceTypes.map((rt) => [rt.value, rt.label]),
        );

        gridOptions.columnDefs = [
            {
                flex: 0,
                maxWidth: 80,
                headerName: '',
                valueGetter: 'node.id',
                sortable: false,
                cellRenderer: indexCellRenderer,
            },
            {
                colId: 'Description',
                sortable: true,
                headerName: 'Description',
                field: 'description',
                sort: { direction: 'asc', priority: 0 },
                cellRenderer: 'nameRenderer',
                cellRendererParams: {
                    to: (id) => `${AppNavPaths.ChargeType}/${id}`,
                    nameField: 'description',
                    idField: 'id',
                    title: 'View this Charge Type',
                },
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilter: true,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'Units.Description',
                sortable: true,
                headerName: 'Charge Units',
                field: 'unitsName',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: unitsFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: unitsFilterParams,
            },
            {
                colId: 'Group',
                sortable: false,
                headerName: 'Eligible Roles',
                field: 'rolesFlat',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: groupFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: groupFilterParams,
            },
            {
                colId: 'EquipmentType',
                headerName: 'Eligible Equipment',
                sortable: false,
                field: 'equipmentFlat',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: equipmentFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: equipmentFilterParams,
            },
            {
                colId: 'IsBillable',
                sortable: true,
                headerName: 'Billable?',
                field: 'isBillable',
                cellRenderer: this.yesNoCellRenderer,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: billableFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: billableFilterParams,
            },
            {
                colId: 'ResourceTypeId',
                headerName: 'Resource Type',
                sortable: false,
                field: 'resourceTypeId',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: resourceTypeFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: resourceTypeFilterParams,
                valueFormatter: ({ value }) => resourceTypeLookup[value] || '',
            },
        ];

        const dataSource = createDataSource(ApiRoutes.chargeType.search(), gridOptions);

        this.setState({
            loading: false,
            gridOptions,
            dataSource,
        });
    }

    yesNoCellRenderer = booleanCellRenderer;

    render() {
        if (this.state.loading) return (<Progress />);

        const { rowData, gridOptions } = this.state;

        return (
            <PageWrap>
                <PageHeading>
                    <FontAwesomeIcon
                        icon={faCoins}
                        className="mr-2 text-muted"
                    />
                    <span>Charge Types</span>
                </PageHeading>
                <DataGridToolbar
                    entity="chargetype"
                    gridApi={this.state.gridApi}
                    dataSource={this.state.dataSource}
                    onAdd={this.onAddChargeType}
                    addLabel="Add Charge Type"
                />
                <DataGrid
                    domLayout="normal"
                    rowData={rowData}
                    gridOptions={gridOptions}
                    gridStatus={this.state.gridStatus}
                />
            </PageWrap>
        );
    }
}
export default ChargeTypeIndex;
